import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  CellChange,
  CellLocation,
  Column,
  Row,
  TextCell
} from '@silevis/reactgrid'
import {
  IPickerList,
  IPickersMap,
  ITableItemsData,
  TAvailableColumnIds,
  TBulkRequestPayloadData,
  TCellInfo,
  TCellTypes,
  TErrorResponseTableData,
  TGridTableFormattedData,
  TTableRows
} from '../types'
import {
  defaultFocusPosition,
  defaultRowData,
  defaultTableCells
} from '../constants'
import {
  checkMissingFieldsValidation,
  getCell,
  getHeaderRow,
  getUpdatedRowInfo,
  parseErrorResponse,
  parseFieldValue,
  parseTableDataToRequest,
  updateRowIdsOnDelete
} from '../helpers'
import { isDeepEqualObjects } from '../../../utils/common'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { fetchVendorFeeTypes } from '../../../pages/CRUDUser/api'
import {
  getVendorContractStewardsRequestAsync,
  getVendorsRequestAsync
} from '../../ContractDetails/api'
import {
  formatOptionIdValue,
  formatOptionNameValue
} from '../../../helper/optionsFormatters'
import useRouter from '../../../hooks/useRouter'
import { routes } from '../../../router'
import { canSeeMyContracts } from '../../../helper/common'
import { getToken } from '../../../redux/store/notifications/getters'
import { bulkContractsCreate } from '../api'
import { setLoading } from '../../../redux/store/common/slice'
import { useBottomNotificationsContext } from '../../BottomNotificationProvider/BottomNotificationsContextProvider'
import {
  TNotificationType,
  TNotificationTypeName
} from '../../BottomNotificationProvider/types'
import { notification } from '../../../components/Notification'
import { VALIDATION_MESSAGES } from '../../../constants'
import { REBATE_CONDITIONS_TYPE_OPTIONS } from '../../ContractDetails/RebateConditions/RebateConditionsForm/constants'
import { TContractCategoriesList } from '../../Categories/types'
import { LED_BY_LABEL } from '../../../components/LeadByIndicator/constants'
import { getCCTableData } from '../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/AddContractCategory/api'
import { CATEGORY_STATUS_NAME } from '../../Categories/constants'

type ContextProps = {
  state: {
    rows: TTableRows
    columns: Column[]
    focusPosition: CellLocation
    rowDeleteConfirmationPopupOpened: boolean
    hasPendingChanges: boolean
    errorLabel: string
    noCategoriesAvailable: boolean
    isUserCanSeeGPOOption?: boolean
  }
  actions: {
    onAddNewRow: () => void
    onCellsChanged: (changes: CellChange[]) => void
    onRowDeleteConfirm: () => void
    onRowDeleteCancel: () => void
    onCancel: () => void
    onSubmit: () => void
  }
}

const ContractBulkTableConfigContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const ContractBulkTableConfigContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const dispatch = useDispatch()
  const registrationId = useSelector(getToken)
  const user = useSelector(getUser)
  const router = useRouter()
  const {
    actions: { openNotification }
  } = useBottomNotificationsContext()

  const tableCells = useMemo(() => {
    return defaultTableCells
  }, [])

  const defaultPickerOptions: ITableItemsData = useMemo(() => {
    return defaultRowData
  }, [])

  const [tableData, setTableData] = useState<ITableItemsData[]>([
    defaultPickerOptions
  ])

  const [errorData, setErrorData] = useState<TErrorResponseTableData>({})
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false)
  const [noCategoriesAvailable, _setNoCategoriesAvailable] =
    useState<boolean>(false)
  const [deleteRowId, setDeleteRowId] = useState<
    TGridTableFormattedData['rowId'] | null
  >(null)
  const [focusPosition, setFocusPosition] =
    useState<CellLocation>(defaultFocusPosition)

  // pickers states
  const [vendorPickerOptions, setVendorPickerOptions] = useState<IPickerList[]>(
    []
  )
  const [contractCategoryPickerOptions, setContractCategoryPickerOptions] =
    useState<IPickerList[]>([])
  const [contractCategoriesList, setContractCategoriesList] = useState<
    TContractCategoriesList[]
  >([])
  const [vendorCSPickerOptionsMap, setVendorCSPickerOptionsMap] =
    useState<IPickersMap>({})
  const [vendorFeeTypesOptionsMap, setVendorFeeTypesOptionsMap] =
    useState<IPickersMap>({})
  const [ledByPickerOptionsMap, setLedByPickerOptionsMap] =
    useState<IPickersMap>({})

  const formattedTableData = useMemo(
    (): TGridTableFormattedData[] =>
      tableData.map<TGridTableFormattedData>((val, idx) => ({
        ...val,
        rowId: idx
      })),
    [tableData]
  )

  const errorLabel = useMemo(() => {
    // if row contains at least one error - we will have key prop in error object
    const erroredRowsCount = Object.keys(errorData).length
    if (!erroredRowsCount) return ''
    return `${
      erroredRowsCount === 1 ? '1 row' : `${erroredRowsCount} rows`
    } contain empty cells or errors.`
  }, [errorData])

  const onCancel = useCallback(
    () =>
      router.push(
        `${routes.contracts}${
          canSeeMyContracts(user.role) ? '/my_contracts' : ''
        }`
      ),
    [router, user.role]
  )

  const onSubmit = useCallback(() => {
    const errors = checkMissingFieldsValidation(tableData)
    if (Object.keys(errors).length) {
      setErrorData(errors)
      return
    }
    setHasPendingChanges(false)
    dispatch(setLoading(true))
    const requestPayload: TBulkRequestPayloadData = {
      contracts: parseTableDataToRequest(tableData),
      firebase_registration_id: registrationId
    }
    bulkContractsCreate(requestPayload)
      .then((response) => {
        if (!registrationId) {
          notification.success({
            message: VALIDATION_MESSAGES.BULK_CREATE_SUCCESS
          })
        } else {
          openNotification(
            {
              id: response.data.task_id,
              numberOfItems: requestPayload.contracts.length,
              type: TNotificationType.INFO,
              date: new Date().toISOString(),
              messageProps: {
                itemUuid: response.data.task_id,
                redirectRoute: `${routes.contracts}${
                  canSeeMyContracts(user.role) ? routes.myContracts : ''
                }`
              }
            },
            TNotificationTypeName.LOADING_CONTRACTS,
            true
          )
        }
        onCancel()
      })
      .catch((err) => setErrorData(parseErrorResponse(err.data)))
      .finally(() => {
        dispatch(setLoading(false))
      })
  }, [
    tableData,
    dispatch,
    registrationId,
    onCancel,
    openNotification,
    user.role
  ])

  const getVendorFeeTypePickerData = useCallback(
    (contract_type: string, rowId: TCellInfo['rowId']) => {
      if (contract_type) {
        fetchVendorFeeTypes(contract_type).then((response) => {
          if (response?.data?.results) {
            setVendorFeeTypesOptionsMap((prev) => ({
              ...prev,
              [rowId]: response.data.results.map(formatOptionIdValue)
            }))
          }
        })
      } else {
        // if any of the fields is empty - need to empty list of vendorCS
        setVendorFeeTypesOptionsMap((prev) => ({
          ...prev,
          [rowId]: undefined
        }))
      }
    },
    []
  )

  const onAddNewRow = useCallback(() => {
    setTableData((prev) => [...prev, defaultPickerOptions])
  }, [defaultPickerOptions])

  const onRowDeleteConfirm = useCallback(
    (rowId?: TGridTableFormattedData['rowId']) => {
      const targetRowId = rowId ?? deleteRowId
      if (targetRowId !== null) {
        setTableData(
          formattedTableData.filter((item) => item.rowId !== targetRowId)
        )
        setErrorData(
          (prev) =>
            updateRowIdsOnDelete(prev, targetRowId) as TErrorResponseTableData
        )
        setVendorCSPickerOptionsMap(
          (prev) => updateRowIdsOnDelete(prev, targetRowId) as IPickersMap
        )
        setVendorFeeTypesOptionsMap(
          (prev) => updateRowIdsOnDelete(prev, targetRowId) as IPickersMap
        )
        setLedByPickerOptionsMap(
          (prev) => updateRowIdsOnDelete(prev, targetRowId) as IPickersMap
        )
        setDeleteRowId(null)
      }
    },
    [formattedTableData, deleteRowId]
  )

  const onRowDeleteCancel = useCallback(() => setDeleteRowId(null), [])

  const rowDeleteConfirmationPopupOpened = useMemo(
    () => deleteRowId !== null,
    [deleteRowId]
  )

  const onRowDelete = useCallback(
    (rowId: TGridTableFormattedData['rowId']) => {
      const deletingRow = formattedTableData.find(
        (item) => item.rowId === rowId
      )
      if (deletingRow) {
        // if row not changed - no need to show confirm popup
        // removing row id and comparing with default empty row
        const deletingRowData: ITableItemsData = { ...deletingRow }
        delete deletingRowData['rowId']
        if (isDeepEqualObjects(deletingRowData, defaultPickerOptions)) {
          onRowDeleteConfirm(rowId)
        } else {
          setDeleteRowId(rowId)
        }
      }
    },
    [
      setDeleteRowId,
      formattedTableData,
      onRowDeleteConfirm,
      defaultPickerOptions
    ]
  )

  const getVendorCSPickerData = useCallback(
    (vendor: string, contract_category: string, rowId: TCellInfo['rowId']) => {
      if (vendor && contract_category) {
        getVendorContractStewardsRequestAsync({
          vendors: [vendor],
          contract_categories: [contract_category]
        }).then((response) => {
          if (response?.data?.results) {
            setVendorCSPickerOptionsMap((prev) => ({
              ...prev,
              [rowId]: response.data.results.map((option) => ({
                ...option,
                label: [option.first_name, option.last_name].join(' '),
                value: option.uuid
              }))
            }))
          }
        })
      } else {
        // if any of the fields is empty - need to empty list of vendorCS
        setVendorCSPickerOptionsMap((prev) => ({
          ...prev,
          [rowId]: undefined
        }))
      }
    },
    []
  )

  const setLedByPickerData = useCallback(
    (cc, rowId) => {
      if (cc) {
        const selectedCC = contractCategoriesList.find(
          (listItem) => listItem.uuid === cc
        )
        setLedByPickerOptionsMap((prev) => ({
          ...prev,
          [rowId]: selectedCC
            ? selectedCC.led_by.map(({ led_by, uuid }) => ({
                label: LED_BY_LABEL[led_by],
                value: uuid
              }))
            : []
        }))
      } else {
        setLedByPickerOptionsMap((prev) => ({
          ...prev,
          [rowId]: undefined
        }))
      }
    },
    [contractCategoriesList]
  )

  const getInitPickersData = useCallback(() => {
    Promise.all([getCCTableData({}), getVendorsRequestAsync()]).then(
      ([contractCategoryResponse, vendorsResponse]) => {
        if (vendorsResponse?.data?.results) {
          setVendorPickerOptions(
            vendorsResponse.data.results.map(formatOptionNameValue)
          )
        }
        if (contractCategoryResponse?.data?.results) {
          if (contractCategoryResponse.data.results.length === 0) {
            _setNoCategoriesAvailable(true)
          }
          setContractCategoryPickerOptions(
            contractCategoryResponse.data.results.map(formatOptionNameValue)
          )
          setContractCategoriesList(contractCategoryResponse?.data?.results)
        }
      }
    )
  }, [])

  const getPickerItemsBasedOnFieldName = useCallback(
    (columnId: TAvailableColumnIds, rowId: TCellInfo['rowId']) => {
      let pickerItems: IPickerList[] | null = []
      switch (columnId) {
        case 'vendor':
          pickerItems = vendorPickerOptions
          break
        case 'contract_category':
          pickerItems = contractCategoryPickerOptions
          break
        case 'vendor_contract_steward':
          pickerItems = vendorCSPickerOptionsMap[rowId] ?? null
          break
        case 'vendor_fee_type':
          pickerItems = vendorFeeTypesOptionsMap[rowId] ?? null
          break
        case 'rebate_type':
          pickerItems = REBATE_CONDITIONS_TYPE_OPTIONS
          break
        case 'led_by':
          pickerItems = ledByPickerOptionsMap[rowId] ?? null
      }
      return pickerItems
    },
    [
      vendorPickerOptions,
      contractCategoryPickerOptions,
      vendorCSPickerOptionsMap,
      vendorFeeTypesOptionsMap,
      ledByPickerOptionsMap
    ]
  )

  const onCellsChanged = useCallback(
    (changes: CellChange<TCellTypes>[]) => {
      setHasPendingChanges(true)
      setTableData([
        ...formattedTableData.map((row) => {
          const specificRowChanges = changes.filter(
            (item) => item.rowId === row.rowId
          )
          const changedFields = parseFieldValue(
            getUpdatedRowInfo(specificRowChanges),
            (colId) => getPickerItemsBasedOnFieldName(colId, row.rowId)
          )

          // if changes contain contact category - change the Led by select options
          if (changedFields.contract_category) {
            const contractCategory =
              changedFields.contract_category ?? row.contract_category
            setLedByPickerData(contractCategory, row.rowId)
            // copy-paste handling
            if (changedFields.led_by === null) {
              const ledByChange = specificRowChanges.find(
                (rowChange) => rowChange.columnId === 'led_by'
              )
              if (ledByChange) {
                const { text } = ledByChange.newCell as TextCell
                changedFields.led_by = Object.keys(LED_BY_LABEL).find(
                  (key) => LED_BY_LABEL[key] === text
                )
              }
            } else if (!changedFields.led_by) {
              // reset all predefined values if another CC was selected
              changedFields.contract_responsible = undefined
              changedFields.contract_category_status = undefined
              changedFields.led_by = undefined
              changedFields.vendor_fee_type = undefined
              setVendorFeeTypesOptionsMap((prev) => ({
                ...prev,
                [row.rowId]: []
              }))
            }
          }
          // if changes contain Led by - change predefined fields (status & contract responsible) and update Vendor fee type select options
          if (changedFields.led_by) {
            const selectedCC = contractCategoriesList.find(
              (listItem) =>
                listItem.uuid ===
                (row.contract_category || changedFields.contract_category)
            )
            // find the right led_by uuid from selected CC array of led_by
            if (
              selectedCC?.led_by.find(
                (ledByItem) => ledByItem.led_by === changedFields.led_by
              )
            ) {
              changedFields.led_by = selectedCC?.led_by.find(
                (ledByItem) => ledByItem.led_by === changedFields.led_by
              )?.uuid
            }
            const selectedLedBy = selectedCC?.led_by.find(
              (ledByItem) => ledByItem.uuid === changedFields.led_by
            )
            if (selectedLedBy) {
              changedFields.contract_category_status = selectedLedBy.status
                ? CATEGORY_STATUS_NAME[selectedLedBy.status]
                : ''
              changedFields.contract_responsible = `${selectedLedBy.responsible.first_name} ${selectedLedBy.responsible.last_name}`
            }
            getVendorFeeTypePickerData(selectedLedBy?.led_by ?? '', row.rowId)
            changedFields.vendor_fee_type = 'no_fee'
          }
          // if changes contain contact category or vendor fields - we need to update vendorCS picker with corresponding options
          if (changedFields.contract_category || changedFields.vendor) {
            const vendor = changedFields.vendor ?? row.vendor
            const contractCategory =
              changedFields.contract_category ?? row.contract_category
            getVendorCSPickerData(vendor, contractCategory, row.rowId)
            changedFields.vendor_contract_steward = undefined
          }
          if (changedFields.rebate_type) {
            changedFields.rebate_type = REBATE_CONDITIONS_TYPE_OPTIONS.find(
              (option) =>
                option.label === changedFields.rebate_type ||
                option.value === changedFields.rebate_type
            )?.value
          }
          if (changedFields.finish_date || changedFields.start_date) {
            const startDate = changedFields.start_date ?? row.start_date
            const endDate = changedFields.finish_date ?? row.finish_date
            if (startDate && endDate && startDate.isAfter(endDate, 'day')) {
              changedFields.finish_date = startDate
            }
          }
          return {
            ...row,
            ...changedFields
          }
        })
      ])
    },
    [
      formattedTableData,
      getPickerItemsBasedOnFieldName,
      getVendorCSPickerData,
      contractCategoriesList,
      getVendorFeeTypePickerData,
      setLedByPickerData
    ]
  )

  const rows = useMemo(
    (): TTableRows => [
      getHeaderRow(tableCells),
      ...formattedTableData.map<Row<TCellTypes>>((row) => ({
        rowId: row.rowId,
        height: 40,
        cells: tableCells.map((cell) =>
          getCell({
            cell,
            rowId: row.rowId,
            row,
            errorCellIds: errorData[row.rowId] ?? [],
            focusChanged: setFocusPosition,
            pickerItems: getPickerItemsBasedOnFieldName(
              cell.columnId,
              row.rowId
            ),
            deletePressed:
              formattedTableData.length > 1 ? onRowDelete : undefined
          })
        )
      }))
    ],
    [
      tableCells,
      formattedTableData,
      errorData,
      getPickerItemsBasedOnFieldName,
      onRowDelete
    ]
  )

  const columns = useMemo(
    (): Column[] =>
      tableCells.map((cell) => ({
        columnId: cell.columnId,
        width: cell.width
      })),
    [tableCells]
  )

  useEffect(() => {
    getInitPickersData()
  }, [getInitPickersData])

  const context = useMemo(
    () => ({
      state: {
        rows,
        columns,
        focusPosition,
        rowDeleteConfirmationPopupOpened,
        hasPendingChanges,
        errorLabel,
        noCategoriesAvailable
      },
      actions: {
        onAddNewRow,
        onCellsChanged,
        onRowDeleteConfirm,
        onRowDeleteCancel,
        onCancel,
        onSubmit
      }
    }),
    [
      errorLabel,
      noCategoriesAvailable,
      onAddNewRow,
      hasPendingChanges,
      onCellsChanged,
      rows,
      columns,
      focusPosition,
      rowDeleteConfirmationPopupOpened,
      onRowDeleteConfirm,
      onRowDeleteCancel,
      onCancel,
      onSubmit
    ]
  )

  return (
    <ContractBulkTableConfigContext.Provider value={context}>
      {children}
    </ContractBulkTableConfigContext.Provider>
  )
}

export const useContractBulkTableConfigContext = () =>
  useContext(ContractBulkTableConfigContext)

export default ContractBulkTableConfigContextProvider
